import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import type {
  PagamentiResponse,
  PaymentOptions,
  WorkOrderPayment,
  WorkOrderPaymentCreate,
  WorkOrderPaymentUpdate,
  IncassaPayload,
} from '@/types/payments'

export const usePaymentsForWorkOrder = (workOrderId: number | undefined) => {
  return useQuery<PagamentiResponse>({
    queryKey: ['payments', workOrderId],
    queryFn: async () => {
      const res = await axiosInstance.get<PagamentiResponse>(
        API_ENDPOINTS.WORK_ORDER_PAYMENTS(workOrderId!)
      )
      return res.data
    },
    enabled: !!workOrderId,
  })
}

export const usePaymentOptions = () => {
  return useQuery<PaymentOptions>({
    queryKey: ['payment-options'],
    queryFn: async () => {
      const res = await axiosInstance.get<PaymentOptions>(API_ENDPOINTS.PAYMENTS_OPTIONS)
      return res.data
    },
    staleTime: Infinity,
  })
}

export const useCreatePayment = (workOrderId: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: WorkOrderPaymentCreate) => {
      const res = await axiosInstance.post<WorkOrderPayment>(
        API_ENDPOINTS.WORK_ORDER_PAYMENTS(workOrderId),
        data
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments', workOrderId] })
    },
    onError: (error: any) => {
      message.error(
        error?.response?.data?.detail ?? 'Errore nella creazione del pagamento'
      )
    },
  })
}

export const useUpdatePayment = (workOrderId: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: WorkOrderPaymentUpdate }) => {
      const res = await axiosInstance.patch<WorkOrderPayment>(
        API_ENDPOINTS.PAYMENT(id),
        data
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments', workOrderId] })
    },
    onError: (error: any) => {
      message.error(
        error?.response?.data?.detail ?? 'Errore nella modifica del pagamento'
      )
    },
  })
}

export const useDeletePayment = (workOrderId: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await axiosInstance.delete(API_ENDPOINTS.PAYMENT(id))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments', workOrderId] })
    },
    onError: (error: any) => {
      message.error(
        error?.response?.data?.detail ?? "Errore nell'eliminazione del pagamento"
      )
    },
  })
}

export const useIncassaPayment = (workOrderId: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: IncassaPayload }) => {
      const res = await axiosInstance.post<WorkOrderPayment>(
        API_ENDPOINTS.PAYMENT_INCASSA(id),
        data
      )
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments', workOrderId] })
    },
    onError: (error: any) => {
      message.error(
        error?.response?.data?.detail ?? "Errore nel registrare l'incasso"
      )
    },
  })
}

